import { IconButton, transition } from '@hover/blueprint';
import React, { useMemo } from 'react';

import { formatVariationEditDescriptions } from 'src/modules/homeownerApp/components/Products/EditDescriptions';
import { useHomeownerTranslate } from 'src/modules/homeownerApp/hooks';
import { useShowManufacturerCTA } from 'src/modules/homeownerApp/hooks/usePartner/useShowManufacturerCTA';
import type { VariationEdit } from 'src/modules/homeownerApp/hooks/useVariationEdits';
import { getDisplayImageTracking } from 'src/modules/homeownerApp/utils/tracking';
import type { AiDisplayImageFragment } from 'src/shared/graphql/generated';

import { ProductVariationProductSwatch } from './ProductVariationProductSwatch';
import type { SwatchCalloutProps } from './SwatchCallout';
import { SwatchCallout } from './SwatchCallout';
import { VariationEditSwatchLabel } from './VariationEditSwatchLabel';

interface VariationEditSwatchCalloutProps
  extends Omit<
    SwatchCalloutProps,
    | 'trackingData'
    | 'manufacturerUrl'
    | 'darkBackgroundColor'
    | 'lightBackgroundColor'
  > {
  image?: AiDisplayImageFragment;
  variationEdit: VariationEdit;
  dominantColor?: string;
}

/**
 * VariationEditSwatchCallout is a wrapper for SwatchCallout using the
 * VariationEdit data source.
 */
export const VariationEditSwatchCallout = ({
  dominantColor,
  image,
  variationEdit,
  ...props
}: VariationEditSwatchCalloutProps) => {
  const t = useHomeownerTranslate();
  const { colorScheme = 'light' } = props;

  const showManufacturerCta = useShowManufacturerCTA();

  const { variationUuid } = variationEdit;
  const { altText, tertiaryIcon } =
    formatVariationEditDescriptions(t, variationEdit) ?? {};
  const manufacturerUrl = showManufacturerCta
    ? variationEdit.variation?.manufacturerUrl ?? undefined
    : undefined;
  const manufacturerName =
    variationEdit.variation?.product.provider?.name ?? undefined;

  const trackingData = useMemo(() => {
    const data = {
      ...(image ? getDisplayImageTracking(image) : {}),
      link_text: altText ?? variationUuid,
      feature: 'material-callouts',
      primary_cta: false,
      track_id: 'get-quote-link',
      provider_name: manufacturerName,
      variation_uuid: variationUuid,
      destination_url: manufacturerUrl,
    };
    return { linkImpression: data, linkPressed: data };
  }, [image, altText, manufacturerUrl, manufacturerName, variationUuid]);

  return (
    <SwatchCallout
      darkBackgroundColor={dominantColor}
      manufacturerUrl={manufacturerUrl}
      {...props}
      trackingData={trackingData}
    >
      {variationEdit.variation && (
        <ProductVariationProductSwatch
          borderRadius={300}
          height={500}
          productVariation={variationEdit.variation}
          region={variationEdit.rawRegion}
          width={500}
        />
      )}
      <VariationEditSwatchLabel
        color={colorScheme === 'dark' ? 'neutral.0' : 'neutral.900'}
        height="100%"
        isDark={colorScheme === 'dark'}
        pl={300}
        showTertiaryLabel={!!manufacturerUrl}
        variationEdit={variationEdit}
      />
      {manufacturerUrl && tertiaryIcon && (
        <IconButton
          _groupHover={{
            color:
              colorScheme === 'dark' ? 'text.primaryInvert' : 'text.primary',
          }}
          background={
            colorScheme === 'dark'
              ? dominantColor || 'neutral.900'
              : 'transparent'
          }
          colorScheme={colorScheme}
          flexShrink={0}
          icon={tertiaryIcon}
          label="manufacture link"
          {...transition.easing250('color')}
        />
      )}
    </SwatchCallout>
  );
};
